import * as React from 'react'

import { Text } from '@buffer-mono/popcorn'

import type { FragmentOf } from '~publish/graphql'

import { useMentionData } from '../context/MentionContext'
import { formatMentionUsername } from '../MentionReplyForm/utils/replyFormUtils'
import { EngagementReplySuggestions } from '../../components/EngagementReplySuggestions'
import type { ReplySuggestion } from '../../components/EngagementReplySuggestions'
import type { EngagementReplySuggestions_SavedReply } from '../../components/EngagementReplySuggestions/EngagementReplySuggestions'
import { useMostSimilarSavedReply } from '../../hooks/savedReplies/useMostSimilarSavedReply'

import styles from './MentionReplyFormHeader.module.css'

import type { JSX } from 'react'

type MentionReplyFormHeaderProps = {
  /**
   * Callback when a suggestion is clicked.
   */
  onSuggestionClick: (suggestionText: string) => boolean
}

/**
 * Mirror of `Comments/CommentsList/CommentReplyFormHeader.tsx`. Always renders the
 * "Replying to @user" label, plus the most-similar saved reply and the AI
 * quick-reply chips above the textarea.
 *
 * Mentions diverge from Comments in where the AI chips come from:
 * Comments lazy-fetch via `commentSuggestions`, but `Mention.suggestions`
 * is delivered as a field on the mention itself (see the `MentionInbox`
 * query). So there's no client-side fetch, loading skeleton, or
 * suggestions paywall here — the field is read directly and rendered when
 * present. The textarea highlight on insertion is owned by
 * `useMentionReplyForm` (triggered through `onSuggestionClick`).
 *
 * Thin data-provider: it reads the mention-side data and feeds the shared
 * `EngagementReplySuggestions` strip, which owns the rendering.
 */
export function MentionReplyFormHeader({
  onSuggestionClick,
}: MentionReplyFormHeaderProps): JSX.Element {
  const mention = useMentionData()

  // `Mention.suggestions` has no per-item id, so key by source + position —
  // stable for a given mention's resolved suggestion list.
  const suggestions: ReplySuggestion[] = (mention.suggestions ?? []).map(
    (suggestion, index) => ({
      id: `${suggestion.source}-${index}`,
      text: suggestion.text,
    }),
  )

  const mostSimilarSavedReply = useMostSimilarSavedReply(mention.text)

  const handleSavedReplyClick = React.useCallback(
    (
      savedReply: FragmentOf<typeof EngagementReplySuggestions_SavedReply>,
    ): boolean => onSuggestionClick(savedReply.content.text),
    [onSuggestionClick],
  )

  const replyingToLabel = mention.author?.username && (
    <Text size="sm" color="subtle" className={styles.replyingToText}>
      Replying to {formatMentionUsername(mention.author.username)}
    </Text>
  )

  return (
    <EngagementReplySuggestions
      replyingToLabel={replyingToLabel}
      suggestions={suggestions}
      mostSimilarSavedReply={mostSimilarSavedReply}
      onSuggestionClick={onSuggestionClick}
      onSavedReplyClick={handleSavedReplyClick}
    />
  )
}
